export type BlogPost = {
  title: string;
  slug: string;
  summary: string;
  category: string;
  tags: string[];
  date: string;
  readTime: string;
  featured: boolean;
  image: string;
  content: string;
};

const modules = import.meta.glob('../content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true
}) as Record<string, string>;

function parseValue(value: string) {
  const trimmed = value.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    return trimmed
      .slice(1, -1)
      .split(',')
      .map((item) => item.trim().replace(/^"|"$/g, ''))
      .filter(Boolean);
  }
  return trimmed.replace(/^"|"$/g, '');
}

function parseMarkdown(raw: string): BlogPost {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) {
    throw new Error('Blog post is missing frontmatter.');
  }

  const [, frontmatter, content] = match;
  const data = frontmatter.split('\n').reduce<Record<string, unknown>>((acc, line) => {
    const index = line.indexOf(':');
    if (index === -1) return acc;
    const key = line.slice(0, index).trim();
    const value = line.slice(index + 1);
    acc[key] = parseValue(value);
    return acc;
  }, {});

  return {
    title: String(data.title),
    slug: String(data.slug),
    summary: String(data.summary),
    category: String(data.category),
    tags: Array.isArray(data.tags) ? (data.tags as string[]) : [],
    date: String(data.date),
    readTime: String(data.readTime),
    featured: Boolean(data.featured),
    image: String(data.image),
    content
  };
}

export const blogPosts = Object.values(modules)
  .map(parseMarkdown)
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

export const featuredPosts = blogPosts.filter((post) => post.featured);

export function getPostBySlug(slug: string) {
  return blogPosts.find((post) => post.slug === slug);
}

export function getRelatedPosts(post: BlogPost) {
  return blogPosts
    .filter((candidate) => candidate.slug !== post.slug)
    .map((candidate) => ({
      post: candidate,
      score:
        (candidate.category === post.category ? 3 : 0) +
        candidate.tags.filter((tag) => post.tags.includes(tag)).length
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((item) => item.post);
}

export const categories = Array.from(new Set(blogPosts.map((post) => post.category))).sort();
